import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { ApiService } from '../core/services/api.service';
import { CountryModel, ProjectModel, ProjectLocationModel, ProjectResponse, UpdateProjectSettingsRequest } from '../models/project.model';
import { BlockScheduleModel, BlockScheduleTemplateModel } from '../models/blockschedule.model';
import { RoleModel } from './user.service';
import { ProjectUserPermissions } from '../models/project-permissions.model';

@Injectable({
    providedIn: 'root'
})
export class ProjectService {
    private projectsSubject = new BehaviorSubject<ProjectModel[]>([]);
    public projects$ = this.projectsSubject.asObservable();

    private currentProjectSubject = new BehaviorSubject<ProjectModel | null>(null);
    public currentProject$ = this.currentProjectSubject.asObservable();

    constructor(private apiService: ApiService) { }

    // Project CRUD Operations

    /**
     * Create a new project
     */
    createProject(projectData: ProjectModel): Observable<number> {
        return this.apiService.post('Project/CreateProject', projectData);
    }

    /**
     * Update project
     */
    updateProject(projectData: ProjectModel): Observable<boolean> {
        return this.apiService.put(`Project/UpdateProject`, projectData);
    }

    /**
     * Get all projects with optional search parameters
     */

    getProjectList(businessUnitId: number = 1): Observable<ProjectModel[]> {
        return this.apiService.get(`Project/GetProjectList?businessUnitId=${businessUnitId}`);
    }

    getProjectLocations(): Observable<ProjectLocationModel[]> {
        return this.apiService.get('Project/GetProjectLocations');
    }

    getCountryList(): Observable<CountryModel[]> {
        return this.apiService.get('Project/GetCountryList');
    }

    getArchivedProjectList(businessUnitId: number = 1): Observable<ProjectModel[]> {
        return this.apiService.get(`Project/GetArchivedProjectList?businessUnitId=${businessUnitId}`);
    }

    getProjectListByUser(userId: number, businessUnitId: number = 1): Observable<ProjectModel[]> {
        return this.apiService.get(`Project/GetProjectListByUser/${userId}?businessUnitId=${businessUnitId}`);
    }

    getRoleList(): Observable<RoleModel[]> {
        return this.apiService.get('Project/GetRoleList');
    }

    getUserProjectPermissions(projectId: number): Observable<ProjectUserPermissions> {
        return this.apiService.get(`Project/GetUserProjectPermissions/${projectId}`);
    }

    getProjectBlockSchedule(projectId: number, includeCustom: boolean = true): Observable<BlockScheduleModel[]> {
        return this.apiService.get(`Project/GetProjectBlockSchedule/${projectId}?includeCustom=${includeCustom}`);
    }

    getProjectBlockScheduleWithoutSpinner(projectId: number, includeCustom: boolean = true): Observable<BlockScheduleModel[]> {
        return this.apiService.getWithoutSpinner(`Project/GetProjectBlockSchedule/${projectId}?includeCustom=${includeCustom}`);
    }

    updateBlockSchedule(templateData: BlockScheduleModel): Observable<ProjectResponse> {
        return this.apiService.post('Project/UpdateBlockSchedule', templateData);
    }

    updateBlockScheduleBatch(schedules: BlockScheduleModel[]): Observable<string[]> {
        return this.apiService.post('Project/UpdateBlockScheduleBatch', schedules);
    }

    addBlockSchedule(templateData: BlockScheduleTemplateModel): Observable<ProjectResponse> {
        return this.apiService.post('Project/AddBlockSchedule', templateData);
    }

    addCustomEquipment(projectId: number): Observable<BlockScheduleModel> {
        return this.apiService.post(`Project/AddCustomEquipment/${projectId}`, {});
    }

    getBlockScheduleTemplate(id: number): Observable<BlockScheduleTemplateModel> {
        return this.apiService.get(`Project/GetBlockScheduleTemplate/${id}`);
    }

    updateBlockScheduleTemplate(templateData: BlockScheduleTemplateModel): Observable<ProjectResponse> {
        return this.apiService.post('Project/UpdateBlockScheduleTemplate', templateData);
    }

    /**
     * Update Skid Spec (Manager Notes)
     */
    updateSkidSpec(data: { projectId?: number; managerNotes?: string }): Observable<boolean> {
        return this.apiService.post('Project/UpdateSkidSpec', data);
    }

    /**
     * Get project by ID
     */
    getProjectById(id: number): Observable<ProjectModel> {
        return this.apiService.get(`Project/GetProjectById/${id}`);
    }



    /**
     * Delete project
     */
    deleteProject(id: number): Observable<any> {
        return this.apiService.delete('projects/${id}');
    }

    /**
     * Archive/Unarchive project (toggle is_active)
     */
    archiveProject(id: number, folderPath?: string): Observable<boolean> {
        return this.apiService.put(`Project/ArchiveProject/${id}`, { folderPath: folderPath ?? null });
    }

    getBackupRootPath(): Observable<{ rootPath: string }> {
        return this.apiService.get('Project/BackupRootPath');
    }

    resyncBackup(projectId: number, folderPath: string): Observable<boolean> {
        return this.apiService.post(`Project/ResyncBackup/${projectId}`, { folderPath });
    }

    updateProjectLeads(projectId: number, request: UpdateProjectSettingsRequest): Observable<boolean> {
        return this.apiService.put(`Project/UpdateProjectLeads/${projectId}`, request);
    }

    getBackupStatus(projectId: number): Observable<any> {
        return this.apiService.get(`Project/BackupStatus/${projectId}`);
    }

    getActiveBackupStatuses(): Observable<any[]> {
        return this.apiService.get('Project/ActiveBackupStatuses');
    }

    // Dropdown Options

    getIncotermList(): Observable<any[]> {
        return this.apiService.get('Project/GetIncotermList');
    }


    getIntegratorList(): Observable<any[]> {
        return this.apiService.get('Project/GetIntegratorList');
    }


    getInverterModelList(): Observable<any[]> {
        return this.apiService.get('Project/GetInverterModelList');
    }


    getTransformerList(): Observable<any[]> {
        return this.apiService.get('Project/GetTransformerList');
    }

    getBsProjectStatusList(): Observable<any[]> {
        return this.apiService.get('Project/GetBsProjectStatusList');
    }

    /**
     * Generate project report
     */
    generateProjectReport(projectId: number): Observable<Blob> {    
        return this.apiService.getFile('projects/${projectId}/report');
    }

    /**
     * Clear current project
     */
    clearCurrentProject(): void {
        this.currentProjectSubject.next(null);
    }

    /**
     * Set current project
     */
    setCurrentProject(project: ProjectModel): void {
        this.currentProjectSubject.next(project);
    }

    /**
     * Get current project value
     */
    getCurrentProject(): ProjectModel | null {
        return this.currentProjectSubject.value;
    }

    /**
     * Check if project exists by number
     */
    checkProjectNumberExists(projectNumber: string): Observable<{ exists: boolean; project: ProjectModel | null }> {
        return this.apiService.get(`Project/CheckProjectNumberExists/${encodeURIComponent(projectNumber)}`);
    }

    /**
     * Get project summary
     */
    getProjectSummary(projectId: number): Observable<any> {
        return this.apiService.get(`projects/${projectId}/summary`);
    }

    addProjectUsers(projectId: number, projectUsers: { userId: number; roleId: number }[]): Observable<boolean> {
        return this.apiService.post(`Project/AddProjectUsers/${projectId}`, projectUsers);
    }

    editProjectUser(projectId: number, userId: number, roleId: number): Observable<boolean> {
        return this.apiService.put(`Project/EditProjectUser/${projectId}/${userId}`, { roleId });
    }

    deleteProjectUser(projectId: number, userId: number): Observable<boolean> {
        return this.apiService.delete(`Project/DeleteProjectUser/${projectId}/${userId}`);
    }

    updateProjectUserStatus(projectId: number, userId: number, isActive: boolean): Observable<boolean> {
        return this.apiService.put(`Project/UpdateUserStatus/${projectId}/${userId}`, { isActive });
    }

    updateProjectUserOnField(projectId: number, userId: number, onField: boolean): Observable<boolean> {
        return this.apiService.put(`Project/UpdateUserOnField/${projectId}/${userId}`, { onField });
    }

    toggleFavouriteProject(projectId: number, userId: number): Observable<boolean> {
        return this.apiService.put(`Project/ToggleFavouriteProject/${projectId}/${userId}`, {});
    }

    downloadBlockSchedule(projectId: any): Observable<any> {
        return this.apiService.getFile('Project/DownloadBlockSchedule/' + projectId)
    }

    downloadBlockScheduleQrLabel(blockScheduleId: number): Observable<Blob> {
        return this.apiService.getBlob(`Project/DownloadBlockScheduleQrLabel/${blockScheduleId}`);
    }

    downloadAllBlockScheduleQrLabels(projectId: number): Observable<Blob> {
        return this.apiService.getBlob(`Project/DownloadAllBlockScheduleQrLabels/${projectId}`);
    }

    downloadSkidSpec(projectId: number): Observable<any> {
        return this.apiService.getFile('Project/DownloadSkidSpec/' + projectId)
    }

    searchBlockScheduleReport(searchModel: any): Observable<any[]> {
        return this.apiService.post('Project/SearchBlockScheduleReport', searchModel);
    }

    downloadBlockScheduleReport(searchModel: any): Observable<any> {
        return this.apiService.postFile('Project/DownloadBlockScheduleReport', searchModel);
    }
}
